import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { Page, SaasInvoice, SaasInvoiceStatus } from '../../api/admin';
import { useLanguage } from '../../contexts/LanguageContext';

const STATUS_TONE: Record<SaasInvoiceStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-700',
  PAID: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  REFUNDED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-slate-100 text-slate-700',
};

const SaasInvoiceList: React.FC = () => {
  const { t } = useLanguage();
  const [data, setData] = useState<Page<SaasInvoice> | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<SaasInvoiceStatus | ''>('');
  const [page, setPage] = useState(0);
  const [running, setRunning] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi
      .listInvoices(status || undefined, page, 20)
      .then(setData)
      .catch((e) => toast.error(e?.message ?? 'Failed'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page]);

  const handleRunBilling = async () => {
    if (!window.confirm(t.admin?.invoices?.runConfirm ?? 'Générer les factures SaaS pour le mois en cours ?')) return;
    setRunning(true);
    try {
      const r = await adminApi.runBillingNow();
      toast.success(`${r.created} ${t.admin?.invoices?.invoicesCreated ?? 'factures générées'}`);
      load();
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">
          {t.admin?.invoices?.title ?? 'Factures SaaS'}
        </h1>
        <button
          onClick={handleRunBilling}
          disabled={running}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
        >
          {running
            ? t.common.loading
            : t.admin?.invoices?.runBilling ?? 'Lancer la facturation du mois'}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as SaasInvoiceStatus | '');
            setPage(0);
          }}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
        >
          <option value="">{t.admin?.invoices?.allStatuses ?? 'Tous les statuts'}</option>
          <option value="PENDING">PENDING</option>
          <option value="PAID">PAID</option>
          <option value="FAILED">FAILED</option>
          <option value="REFUNDED">REFUNDED</option>
          <option value="CANCELLED">CANCELLED</option>
        </select>
      </div>

      {loading && <div className="text-slate-500">{t.common.loading}</div>}

      {data && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  {t.admin?.invoices?.col?.company ?? 'Abonné'}
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  {t.admin?.invoices?.col?.period ?? 'Période'}
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Plan</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">
                  {t.admin?.invoices?.col?.amount ?? 'TTC'}
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Statut</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  {t.admin?.invoices?.col?.payment ?? 'Lien paiement'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.content.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    {t.common.noResults}
                  </td>
                </tr>
              )}
              {data.content.map((inv) => (
                <tr key={inv.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/subscribers/${inv.companyId}`}
                      className="text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      {inv.companyName}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {inv.periodStart} → {inv.periodEnd}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{inv.plan}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">
                    {inv.amountTtc.toFixed(3)} TND
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${STATUS_TONE[inv.status]}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {inv.paymentUrl ? (
                      <a
                        href={inv.paymentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-indigo-600 hover:text-indigo-700 underline"
                      >
                        {t.admin?.invoices?.openLink ?? 'Ouvrir'}
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {data.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
              <div className="text-slate-500">
                {t.common.page} {data.number + 1} {t.common.of} {data.totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={data.number === 0}
                  className="px-3 py-1 border border-slate-300 rounded text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  {t.common.previous}
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={data.number + 1 >= data.totalPages}
                  className="px-3 py-1 border border-slate-300 rounded text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  {t.common.next}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SaasInvoiceList;
